export async function initializeProjective3D() {
    if (this.projective3D) {
        return;
    }

    if (!this.mesh) {
        console.warn('Cannot initialize 3D projection: No mesh loaded');
        this.updateStatus('Load a 3D model first to enable 3D projection');
        return;
    }

    if (!this.renderer) {
        console.warn('Cannot initialize 3D projection: No renderer available');
        this.updateStatus('Error: Renderer not available');
        return;
    }

    try {
        const module = await import('../ProjectiveBaker3D.js');
        const { ProjectiveBaker3D } = module;
        this.projective3D = new ProjectiveBaker3D(this);
        await this.projective3D.initializeDependencies();
        await this.projective3D.setMesh(this.mesh);
    } catch (error) {
        console.error('Failed to initialize 3D projection system:', error);
        this.updateStatus(`Error: Failed to load 3D projection system - ${error.message}`);

        setTimeout(() => {
            const mode2DBtn = document.getElementById('compactMode2D');
            if (mode2DBtn) {
                mode2DBtn.click();
                this.updateStatus('Switched back to 2D mode due to 3D initialization error');
            }
        }, 1000);
    }
}

export async function setProjectionMode(mode) {
    if (mode !== '2d' && mode !== '3d' && mode !== 'brush') {
        console.warn('Invalid projection mode:', mode);
        return;
    }

    this.projectionMode = mode;

    if (mode === '2d') {
        if (this.projective3D) {
            this.projective3D.removeProjector?.();
        }
        if (this.controls) this.controls.enabled = true;
        this.refreshCharacterMaterials();
        this.updateStatus('2D UV projection mode active');
    } else if (mode === '3d') {
        this.removeMoveableDecal();
        if (this.controls) this.controls.enabled = true;

        if (!this.projective3D) {
            if (this.mesh) {
                await this.initializeProjective3D();
            } else {
                this.updateStatus('Please load a 3D model first to use 3D projection');
                setTimeout(() => {
                    const mode2DBtn = document.getElementById('compactMode2D');
                    if (mode2DBtn) mode2DBtn.click();
                }, 100);
                return;
            }
        } else {
            this.updateStatus('3D world projection mode active');
        }
    } else if (mode === 'brush') {
        this.removeMoveableDecal();
        if (this.projective3D) {
            this.projective3D.removeProjector?.();
        }
        if (!this.brushSystem) {
            this.initializeBrushSystem();
        }
        if (this.controls) this.controls.enabled = true;
        this.refreshCharacterMaterials();
        this.updateStatus('Brush painting mode active - Left click to paint/erase, X to toggle modes');
    }
}

export function create3DProjector(position, target) {
    if (!this.projective3D) {
        this.updateStatus('Initializing 3D projection system...');
        if (this.mesh) {
            this.initializeProjective3D();
            setTimeout(() => {
                if (this.projective3D) {
                    this.create3DProjector(position, target);
                } else {
                    this.updateStatus('Failed to initialize 3D projection system');
                }
            }, 500);
        } else {
            this.updateStatus('Load a 3D model first to use 3D projection');
        }
        return;
    }

    if (!this.decalTexture) {
        this.updateStatus('No decal texture loaded');
        return;
    }

    this.projective3D.setDecalTexture(this.decalTexture);
    this.projective3D.createProjector(position, target);
    this.updateStatus('3D projector created! Use transform controls to position it, then click "Apply Decal" to bake.');
}

export async function apply3DProjection() {
    if (!this.projective3D) {
        this.updateStatus('3D projection system not initialized');
        return;
    }

    await this.projective3D.bakeProjection();
    this.refreshCharacterMaterials();
}

export function cancel3DProjection() {
    if (this.projective3D) {
        this.projective3D.removeProjector?.();
    }
    this.refreshCharacterMaterials();
}

export function set3DProjectorSize(size) {
    if (this.projective3D) {
        this.projective3D.setProjectorSize?.(size);
    }
}

export function set3DProjectorAspect(aspect) {
    if (this.projective3D) {
        this.projective3D.setProjectorAspect?.(aspect);
    }
}

export function set3DProjectionOpacity(opacity) {
    if (this.projective3D) {
        this.projective3D.setOpacity?.(opacity);
    }
}

export function set3DProjectionFalloff(falloff) {
    if (this.projective3D) {
        this.projective3D.setFalloff?.(falloff);
    }
}
